"use client";

import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";
import { isPublic } from "@/lib/profile";
import { PRIVATE_EDGE_CLASSES } from "@/components/scene/PrivateOnlyMark";
import { ALL_MODULES, type Module } from "@/lib/modules";

export type { Module };

type Pillar = "verify" | "trace" | "finance";

const MODULE_PILLAR: Record<Module, Pillar> = {
  Profile:                  "verify",
  GLEIF:                    "verify",
  Counterparty:             "verify",
  Risk:                     "verify",
  IdentityVerification:     "verify",
  CrossBorderCompliance:    "verify",
  TradePipeline:            "trace",
  Logistics:                "trace",
  ReconciliationDocs:       "trace",
  EInvoicing:               "finance",
  BillingAndInvoices:       "finance",
  ReconciliationSettlement: "finance",
};

const PILLAR_TOKEN: Record<Pillar, string> = {
  verify:  "var(--state-verified)",
  trace:   "var(--state-traced)",
  finance: "var(--state-financed)",
};

const FLOURISH_MS = 600; // window during which a freshly-unlocked tile glows

/**
 * Left-rail dashboard showing the persona's module tiles. Modules unlock
 * progressively as the user advances — driven by ui.ModuleUnlocked events
 * accumulating in useSSE.modulesUnlocked.
 *
 *   variant = "exporter" → Aurora's dashboard (all modules pre-lit by default)
 *   variant = "importer" → Al Madar's dashboard (all dim until unlocked)
 *   variant = "ghost"    → for the "behind the modal" preview at screen 0.6
 *
 * When a module flips from dim → lit, the tile fires a 600 ms scale-in +
 * brand-teal halo flourish (paired with the moduleUnlock audio cue
 * dispatched by useAudioCues). Restraint principle preserved — Fin-Game
 * is "capability appearing", not confetti.
 */
export function Dashboard({
  unlocked,
  variant,
  highlight,
  className,
}: {
  unlocked: Set<string>;
  variant: "exporter" | "importer" | "ghost";
  highlight?: Module;
  className?: string;
}) {
  const t = useTranslations("modules");
  const tPillars = useTranslations("pillars");
  const tD = useTranslations("dashboard");
  const [flourishing, setFlourishing] = useState<Set<string>>(new Set());
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Detect newly-unlocked modules. The exporter variant pre-lights
    // everything, so it never flourishes — Aurora's already established.
    if (variant === "exporter") return;
    const fresh: string[] = [];
    unlocked.forEach((m) => {
      if (!seenRef.current.has(m)) {
        seenRef.current.add(m);
        fresh.push(m);
      }
    });
    if (!fresh.length) return;
    setFlourishing((prev) => new Set([...prev, ...fresh]));
    const timers = fresh.map((m) =>
      window.setTimeout(() => {
        setFlourishing((prev) => {
          const next = new Set(prev);
          next.delete(m);
          return next;
        });
      }, FLOURISH_MS),
    );
    return () => { timers.forEach(window.clearTimeout); };
  }, [unlocked, variant]);

  return (
    <aside className={clsx(
      "flex h-full min-h-0 w-44 shrink-0 flex-col border-e border-state-verified/20 bg-orbitra-ink/60 px-3 py-4 backdrop-blur",
      className,
    )}>
      <p className="mb-3 shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{tD("modulesKicker")}</p>
      {/* min-h-0 + overflow-y-auto so the 12-tile list scrolls when the
       *  viewport is short rather than overflowing the SceneRouter pane. */}
      <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-1">
        {ALL_MODULES.filter((m) => !(isPublic() && m === "CrossBorderCompliance")).map((m) => {
          const isLit = variant === "exporter" || unlocked.has(m);
          const isHi  = highlight === m;
          const isFlourishing = flourishing.has(m);
          const pillar = MODULE_PILLAR[m];
          return (
            <li key={m}>
              <ModuleTile
                name={m}
                label={t(m, { fallback: m })}
                pillarLabel={tPillars(pillar, { fallback: pillar })}
                pillarToken={PILLAR_TOKEN[pillar]}
                lit={isLit}
                active={isHi}
                ghost={variant === "ghost"}
                flourishing={isFlourishing}
                privateOnly={m === "CrossBorderCompliance"}
              />
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function ModuleTile({
  label, pillarLabel, pillarToken, lit, active, ghost, flourishing, privateOnly = false,
}: {
  name: Module;
  label: string;
  pillarLabel: string;
  pillarToken: string;
  lit: boolean;
  active: boolean;
  ghost: boolean;
  flourishing: boolean;
  privateOnly?: boolean;
}) {
  const tD = useTranslations("dashboard");
  const markPrivate = privateOnly && !isPublic();
  return (
    <div
      className={clsx(
        // 4 px left-edge accent + extra start-padding for the active tile
        // so the audience reads position-in-the-list at a glance on a
        // projector. Default tiles keep py-1.5; active tiles inherit
        // the same vertical rhythm.
        "relative rounded-md border py-1.5 pe-2 ps-2 text-[11px] transition-state",
        lit
          ? "border-state-verified/40 bg-state-verified/10 text-orbitra-paper"
          : "border-orbitra-muted/20 bg-orbitra-ink/30 text-orbitra-muted",
        // Active treatment — visibly louder than the lit-but-inactive
        // baseline. Brighter background + bolder font + left accent.
        active && [
          "border-state-verified bg-state-verified/30 font-semibold text-orbitra-paper",
          "shadow-[0_0_16px_var(--state-verified-glow),0_0_3px_var(--state-verified)]",
          "ps-3",
          "before:absolute before:inset-y-1 before:start-0 before:w-[3px] before:rounded-r-sm before:bg-state-verified before:shadow-[0_0_6px_var(--state-verified)]",
        ],
        ghost && !lit && "opacity-40",
        flourishing && "animate-tile-flourish",
        markPrivate && PRIVATE_EDGE_CLASSES,
      )}
      style={
        flourishing
          ? { boxShadow: "0 0 24px var(--state-verified-glow), 0 0 6px var(--state-verified)" }
          : undefined
      }
    >
      <p
        className="font-mono text-[8px] font-semibold uppercase tracking-[0.22em]"
        style={{ color: lit ? pillarToken : undefined }}
      >
        {pillarLabel}
      </p>
      <div className="mt-0.5 flex items-center gap-2">
        <span
          className={clsx(
            "inline-block h-1.5 w-1.5 rounded-full",
            !lit && "bg-orbitra-muted/40",
            active && "shadow-[0_0_8px_var(--state-verified)]",
            flourishing && "shadow-[0_0_12px_var(--state-verified)]",
          )}
          style={lit ? { backgroundColor: pillarToken } : undefined}
        />
        <span className="truncate">{label}</span>
        {!lit && <span className="ms-auto text-[9px] text-orbitra-muted/70">⌐</span>}
        {active && !flourishing && <span aria-hidden="true" className="ms-auto text-[10px] font-mono text-state-verified">●</span>}
        {flourishing && <span className="ms-auto text-[9px] font-mono text-state-verified">{tD("unlockTag")}</span>}
      </div>
    </div>
  );
}
